import React from "react";
import * as Sentry from "@sentry/remix";
import { useFieldArray, useFormState, useWatch } from "react-hook-form";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import {
  Box,
  Text,
  HStack,
  Stack,
  VStack,
  Wrap,
  WrapItem,
  Button,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Checkbox,
  FormControl,
  FormLabel,
  Menu,
  MenuButton,
  MenuList,
  MenuOptionGroup,
  MenuItemOption,
  Tag,
  TagLabel,
  TagCloseButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  useColorMode,
  useDisclosure,
} from "@chakra-ui/react";
import { MdExpandMore, MdClose, MdAdd, MdSearch } from "react-icons/md";

import { resolvePresetToNights } from "#app/config/tripPlannerPresets.js";
import {
  CUSTOM_ROUTE,
  SINGLE_NIGHT_ROUTE,
  getNightLabels,
  getSingleNightRoute,
} from "#app/utils/tripPlannerForm.js";

/**
 * TripBuilder — Night-by-night timeline for trip planner mode.
 *
 * Props:
 *   formContext  — react-hook-form's useForm() return
 *   facility     — facility object with .sites[]
 *   presets      — tripPlannerPresets entry (may be null)
 *   mode         — "create" | "edit" | "clone"
 */
export function TripBuilder({ formContext, facility, presets, mode }) {
  const { control, setValue, getValues, trigger } = formContext;

  React.useEffect(() => {
    const replay = Sentry.getReplay();
    if (replay && !replay.getReplayId()) {
      replay.start();
    }
  }, []);

  // The server rejects a route with an empty night, so catch it before submit
  // and let RHF block the submit like any other invalid field.
  const { fields, append, remove } = useFieldArray({
    control,
    name: "tripNights",
    rules: {
      validate: (nights) =>
        (nights?.length > 0 &&
          nights.every((night) => night.siteIds?.length > 0)) ||
        "Select at least one site for each night",
    },
  });

  // RHF re-checks the route's rule on submit and after add/remove, but not
  // after a night's sites change or a preset replaces the route, and it never
  // clears the route error on its own once the route is valid again.
  const { submitCount } = useFormState({ control });
  const revalidateRoute = () => {
    if (submitCount > 0) trigger("tripNights");
  };

  const tripNights = useWatch({ control, name: "tripNights" });
  React.useEffect(() => {
    const allSiteIds = [
      ...new Set((tripNights || []).flatMap((n) => n.siteIds)),
    ];
    setValue("selectedSiteIds", allSiteIds, { shouldDirty: false });
  }, [tripNights, setValue]);

  const siteOptions = React.useMemo(
    () =>
      facility.sites.map((site) => ({
        value: site.id,
        label: site.name,
        group: site.group || null,
      })),
    [facility.sites],
  );

  const wholeTripOnly = !!presets?.wholeTripOnly;

  // Route presets — resolve siteNames to siteIds once
  const { routeOptions, resolvedRoutes } = React.useMemo(() => {
    if (!presets?.routes) return { routeOptions: [], resolvedRoutes: {} };
    const resolved = {};
    const options = [];
    for (const [key, route] of Object.entries(presets.routes)) {
      options.push({
        value: key,
        label: route.label,
        description: route.description,
      });
      resolved[key] = resolvePresetToNights(route, facility.sites);
    }
    return { routeOptions: options, resolvedRoutes: resolved };
  }, [presets, facility.sites]);

  // Name the current nights: a matching preset, the one-night route, or custom
  const detectRoute = React.useCallback(
    (nights) => {
      for (const [key, presetNights] of Object.entries(resolvedRoutes)) {
        if (nights.length !== presetNights.length) continue;
        const match = nights.every((night, i) => {
          const preset = presetNights[i];
          if (night.siteIds.length !== preset.siteIds.length) return false;
          const a = [...night.siteIds].sort();
          const b = [...preset.siteIds].sort();
          return a.every((id, j) => id === b[j]);
        });
        if (match) return key;
      }
      if (!wholeTripOnly && nights.length === 1) return SINGLE_NIGHT_ROUTE;
      return CUSTOM_ROUTE;
    },
    [resolvedRoutes, wholeTripOnly],
  );

  const [selectedRoute, setSelectedRoute] = React.useState(() =>
    detectRoute(getValues("tripNights") || []),
  );

  // A filled-in preset shows as one line with a Change button, so the start
  // dates stay near the top of the page on a phone. Anything else opens
  // straight onto the night-by-night cards.
  const [expanded, setExpanded] = React.useState(
    () => !resolvedRoutes[selectedRoute],
  );

  // The loaders always send a route; seed one night if one ever arrives empty
  const didInitRef = React.useRef(false);
  React.useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    if (fields.length > 0) return;

    const nights = getSingleNightRoute();
    setValue("tripNights", nights, { shouldDirty: false });
    setSelectedRoute(detectRoute(nights));
  }, []);

  const handleRouteChange = (routeKey) => {
    setSelectedRoute(routeKey);

    if (routeKey === CUSTOM_ROUTE) {
      // Custom starts from the current route rather than wiping it
      if (!(getValues("tripNights")?.length > 0)) {
        setValue("tripNights", getSingleNightRoute(), { shouldDirty: true });
      }
      revalidateRoute();
      return;
    }

    const nights =
      routeKey === SINGLE_NIGHT_ROUTE
        ? getSingleNightRoute()
        : resolvedRoutes[routeKey];
    if (nights) {
      setValue("tripNights", nights, { shouldDirty: true });
      revalidateRoute();
    }
  };

  const syncRoute = () => {
    setSelectedRoute(detectRoute(getValues("tripNights") || []));
  };

  const handleNightModified = () => {
    syncRoute();
    revalidateRoute();
  };

  // A new night starts with the previous night's sites: staying another night
  // at a base camp needs no picking, and a new hut is one change away.
  const handleAddNight = () => {
    const nights = getValues("tripNights") || [];
    const previous = nights[nights.length - 1];
    append({ index: nights.length, siteIds: [...(previous?.siteIds || [])] });
    handleNightModified();
  };

  const handleRemoveNight = (nightIndex) => {
    remove(nightIndex);
    // Re-index remaining nights
    const current = getValues("tripNights");
    const reindexed = current.map((night, i) => ({ ...night, index: i }));
    setValue("tripNights", reindexed, { shouldDirty: true });
    handleNightModified();
  };

  const allRouteOptions = React.useMemo(
    () => [
      ...routeOptions,
      ...(wholeTripOnly
        ? []
        : [
            {
              value: SINGLE_NIGHT_ROUTE,
              label: "One night",
              description: "Pick the sites you'd take for a single night",
            },
          ]),
      {
        value: CUSTOM_ROUTE,
        label: "Custom",
        description: "Build your own route, night by night",
      },
    ],
    [routeOptions, wholeTripOnly],
  );

  const { colorMode } = useColorMode();

  if (!expanded) {
    const preset = presets.routes[selectedRoute];
    return (
      <RouteSummary
        label={preset.label}
        description={
          preset.description ||
          getNightLabels(tripNights, facility.sites).join(" → ")
        }
        nightCount={fields.length}
        showFilledNotice={mode !== "edit"}
        onChange={() => setExpanded(true)}
      />
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      <RouteSelector
        options={allRouteOptions}
        value={selectedRoute}
        onChange={handleRouteChange}
      />

      <VStack
        spacing={3}
        align="stretch"
        borderLeftWidth="2px"
        borderLeftColor={colorMode === "light" ? "gray.200" : "whiteAlpha.200"}
        ml={2}
        pl={3}
      >
        {fields.map((field, index) => (
          <NightCard
            key={field.id}
            index={index}
            control={control}
            siteOptions={siteOptions}
            canRemove={fields.length > 1}
            onRemove={() => handleRemoveNight(index)}
            onModify={handleNightModified}
            formContext={formContext}
            submitCount={submitCount}
          />
        ))}

        <Button
          variant="outline"
          size="sm"
          leftIcon={<MdAdd />}
          onClick={handleAddNight}
          alignSelf="flex-start"
        >
          Add night
        </Button>
      </VStack>
    </VStack>
  );
}

function RouteSummary({
  label,
  description,
  nightCount,
  showFilledNotice,
  onChange,
}) {
  const { colorMode } = useColorMode();

  return (
    <Box
      borderLeftWidth="3px"
      borderLeftColor="accent"
      bg={colorMode === "light" ? "gray.50" : "whiteAlpha.50"}
      borderRadius="md"
      px={4}
      py={3}
      data-testid="trip-route-summary"
    >
      <Text
        fontSize="xs"
        fontWeight="bold"
        textTransform="uppercase"
        letterSpacing="wide"
        color="muted"
        mb={1}
      >
        Route
      </Text>
      <Stack
        direction={{ base: "column", sm: "row" }}
        justify="space-between"
        align="flex-start"
        spacing={3}
      >
        <Box minW={0}>
          <Text fontWeight="medium">{label}</Text>
          <Text fontSize="sm" color="muted">
            {nightCount} {nightCount === 1 ? "night" : "nights"} · {description}
          </Text>
        </Box>
        <Button size="sm" variant="outline" flexShrink={0} onClick={onChange}>
          Change route
        </Button>
      </Stack>
      {showFilledNotice && (
        <Text fontSize="xs" color="muted" mt={2}>
          We've filled in this route for you. Change it if your trip is
          different.
        </Text>
      )}
    </Box>
  );
}

function RouteSelector({ options, value, onChange, ...rest }) {
  const { colorMode } = useColorMode();
  const selected = options.find((o) => o.value === value);
  const selectedLabel = selected?.label || "Select a route...";

  return (
    <FormControl
      {...rest}
      borderLeftWidth="3px"
      borderLeftColor="accent"
      bg={colorMode === "light" ? "gray.50" : "whiteAlpha.50"}
      borderRadius="md"
      px={4}
      py={3}
    >
      <FormLabel
        fontSize="xs"
        fontWeight="bold"
        textTransform="uppercase"
        letterSpacing="wide"
        color="muted"
        mb={2}
      >
        Route
      </FormLabel>
      <Menu>
        <MenuButton
          as={Button}
          variant="outline"
          size="md"
          w="100%"
          textAlign="left"
          fontWeight="normal"
          rightIcon={<MdExpandMore />}
        >
          <Text noOfLines={1}>{selectedLabel}</Text>
        </MenuButton>
        <MenuList maxH="300px" maxW="min(320px, 90vw)" overflowY="auto">
          <MenuOptionGroup type="radio" value={value} onChange={onChange}>
            {options.map(({ label, value: optionValue, description }) => (
              <MenuItemOption
                key={optionValue}
                value={optionValue}
                fontSize="sm"
                whiteSpace="normal"
                sx={{ "& > .chakra-menu__icon-wrapper": { color: "accent" } }}
              >
                <Text>{label}</Text>
                {description && (
                  <Text fontSize="xs" color="muted">
                    {description}
                  </Text>
                )}
              </MenuItemOption>
            ))}
          </MenuOptionGroup>
        </MenuList>
      </Menu>
      {selected?.description && (
        <Text fontSize="xs" color="muted" mt={1.5}>
          {selected.description}
        </Text>
      )}
    </FormControl>
  );
}

function NightCard({
  index,
  control,
  siteOptions,
  canRemove,
  onRemove,
  onModify,
  formContext,
  submitCount,
}) {
  const { colorMode } = useColorMode();

  const siteIds =
    useWatch({ control, name: `tripNights.${index}.siteIds` }) || [];

  // An empty night only shows as an error once the user has emptied it or a
  // submit has tried to send it. A night that starts empty (a facility with
  // no preset, "Custom", "Add night") is a prompt, not a mistake.
  const [edited, setEdited] = React.useState(false);
  const [submitCountAtMount] = React.useState(submitCount);
  const isEmpty = siteIds.length === 0;
  const hasError = isEmpty && (edited || submitCount > submitCountAtMount);

  const handleToggleSite = (siteId) => {
    setEdited(true);
    const numericId = typeof siteId === "string" ? parseInt(siteId) : siteId;
    const current = formContext.getValues(`tripNights.${index}.siteIds`) || [];

    if (current.includes(numericId)) {
      formContext.setValue(
        `tripNights.${index}.siteIds`,
        current.filter((id) => id !== numericId),
        { shouldDirty: true },
      );
    } else {
      formContext.setValue(
        `tripNights.${index}.siteIds`,
        [...current, numericId],
        { shouldDirty: true },
      );
    }
    onModify?.();
  };

  const handleMenuChange = (values) => {
    setEdited(true);
    const numericIds = values.map((v) =>
      typeof v === "string" ? parseInt(v) : v,
    );
    formContext.setValue(`tripNights.${index}.siteIds`, numericIds, {
      shouldDirty: true,
    });
    onModify?.();
  };

  const siteNameMap = React.useMemo(
    () => new Map(siteOptions.map(({ value, label }) => [value, label])),
    [siteOptions],
  );

  return (
    <Box
      data-trip-night-empty={isEmpty ? "" : undefined}
      borderWidth="1px"
      borderRadius="md"
      borderColor={hasError ? "error" : "border"}
      _hover={{ boxShadow: "sm" }}
      p={4}
      position="relative"
    >
      {canRemove && (
        <IconButton
          icon={<MdClose />}
          size="sm"
          variant="ghost"
          aria-label={`Remove night ${index + 1}`}
          onClick={onRemove}
          position="absolute"
          top={1}
          right={1}
        />
      )}
      <Text fontWeight="medium" fontSize="sm" mb={1}>
        Night {index + 1}
        <Text as="span" fontSize="xs" color="muted">
          {" "}
          · any of these sites
        </Text>
      </Text>

      <SitePicker
        siteIds={siteIds}
        siteOptions={siteOptions}
        siteNameMap={siteNameMap}
        hasError={hasError}
        onToggleSite={handleToggleSite}
        onSetSites={handleMenuChange}
        colorMode={colorMode}
      />

      {hasError && (
        <Text color="error" fontSize="xs" mt={2}>
          Select at least one site for this night
        </Text>
      )}
    </Box>
  );
}

function SiteCheckboxRow({
  value,
  label,
  search,
  checkedSet,
  onToggleSite,
  colorMode,
}) {
  const matches = match(label, search, { insideWords: true });
  const parts = parse(label, matches);

  return (
    <Box
      key={value}
      as="label"
      display="flex"
      alignItems="center"
      px={3}
      py={1.5}
      cursor="pointer"
      borderRadius="md"
      _hover={{
        bg: colorMode === "light" ? "gray.100" : "whiteAlpha.100",
      }}
    >
      <Checkbox
        isChecked={checkedSet.has(value)}
        onChange={() => onToggleSite(value)}
        colorScheme="brand"
        size="sm"
        mr={2}
      />
      <Text fontSize="sm">
        {parts.map(({ text, highlight }, i) => (
          <Text key={i} as="span" fontWeight={highlight ? "bold" : "normal"}>
            {text}
          </Text>
        ))}
      </Text>
    </Box>
  );
}

function SitePicker({
  siteIds,
  siteOptions,
  siteNameMap,
  hasError,
  onToggleSite,
  onSetSites,
  colorMode,
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [search, setSearch] = React.useState("");
  const searchRef = React.useRef(null);

  const checkedSet = React.useMemo(() => new Set(siteIds), [siteIds]);

  const filteredOptions = React.useMemo(() => {
    if (!search.trim()) return siteOptions;
    const q = search.toLowerCase();
    return siteOptions.filter(({ label }) => label.toLowerCase().includes(q));
  }, [siteOptions, search]);

  // Group filtered options by site.group when multiple groups exist
  const { groups, hasMultipleGroups } = React.useMemo(() => {
    const grouped = new Map();
    for (const opt of filteredOptions) {
      const key = opt.group || "All Sites";
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(opt);
    }
    return { groups: grouped, hasMultipleGroups: grouped.size > 1 };
  }, [filteredOptions]);

  const handleOpen = () => {
    setSearch("");
    onOpen();
  };

  const handleClear = () => {
    onSetSites([]);
  };

  const handleSelectGroup = (groupValues) => {
    const newIds = [...new Set([...siteIds, ...groupValues])];
    onSetSites(newIds);
  };

  const handleDeselectGroup = (groupValues) => {
    const removeSet = new Set(groupValues);
    onSetSites(siteIds.filter((id) => !removeSet.has(id)));
  };

  const countLabel =
    siteIds.length === 0
      ? "Select sites..."
      : `${siteIds.length} of ${siteOptions.length} sites selected`;

  return (
    <Box>
      <Popover
        isOpen={isOpen}
        onOpen={handleOpen}
        onClose={onClose}
        placement="bottom-start"
        initialFocusRef={searchRef}
        isLazy
      >
        <PopoverTrigger>
          <Button
            variant="outline"
            size="sm"
            w="100%"
            textAlign="left"
            fontWeight="normal"
            rightIcon={<MdExpandMore />}
            borderColor={hasError ? "error" : undefined}
            justifyContent="space-between"
          >
            <Text noOfLines={1}>{countLabel}</Text>
          </Button>
        </PopoverTrigger>
        <PopoverContent w="100%" maxW="360px">
          <PopoverBody p={0}>
            <Box px={3} pt={3} pb={2}>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <MdSearch color="gray.400" />
                </InputLeftElement>
                <Input
                  ref={searchRef}
                  placeholder="Filter sites..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  variant="filled"
                  borderRadius="md"
                />
              </InputGroup>
            </Box>
            <Box
              maxH={hasMultipleGroups ? "300px" : "200px"}
              overflowY="auto"
              px={1}
            >
              {filteredOptions.length === 0 ? (
                <Text fontSize="sm" color="muted" px={3} py={2}>
                  No sites match "{search}"
                </Text>
              ) : hasMultipleGroups ? (
                [...groups.entries()].map(([groupName, groupOpts]) => {
                  const groupValues = groupOpts.map((o) => o.value);
                  const checkedCount = groupValues.filter((v) =>
                    checkedSet.has(v),
                  ).length;

                  return (
                    <Box key={groupName}>
                      <HStack
                        px={3}
                        py={1.5}
                        position="sticky"
                        top={0}
                        bg={colorMode === "light" ? "gray.50" : "gray.800"}
                        zIndex={1}
                        borderBottomWidth="1px"
                        borderColor={
                          colorMode === "light" ? "gray.100" : "whiteAlpha.100"
                        }
                        justify="space-between"
                      >
                        <HStack spacing={1.5} minW={0}>
                          <Text
                            fontSize="xs"
                            fontWeight="semibold"
                            textTransform="uppercase"
                            letterSpacing="wide"
                            color="emphasized"
                            noOfLines={1}
                          >
                            {groupName}
                          </Text>
                          <Text fontSize="xs" color="muted" flexShrink={0}>
                            ({checkedCount}/{groupOpts.length})
                          </Text>
                        </HStack>
                        <HStack spacing={0} flexShrink={0}>
                          <Button
                            variant="link"
                            size="xs"
                            color="accent"
                            fontWeight="medium"
                            onClick={() => handleSelectGroup(groupValues)}
                          >
                            All
                          </Button>
                          <Text fontSize="xs" color="muted" mx={1}>
                            /
                          </Text>
                          <Button
                            variant="link"
                            size="xs"
                            color="muted"
                            fontWeight="medium"
                            onClick={() => handleDeselectGroup(groupValues)}
                          >
                            None
                          </Button>
                        </HStack>
                      </HStack>
                      {groupOpts.map(({ label, value }) => (
                        <SiteCheckboxRow
                          key={value}
                          value={value}
                          label={label}
                          search={search}
                          checkedSet={checkedSet}
                          onToggleSite={onToggleSite}
                          colorMode={colorMode}
                        />
                      ))}
                    </Box>
                  );
                })
              ) : (
                filteredOptions.map(({ label, value }) => (
                  <SiteCheckboxRow
                    key={value}
                    value={value}
                    label={label}
                    search={search}
                    checkedSet={checkedSet}
                    onToggleSite={onToggleSite}
                    colorMode={colorMode}
                  />
                ))
              )}
            </Box>
            <HStack
              px={3}
              py={2}
              borderTopWidth="1px"
              borderColor="border"
              justifyContent="space-between"
            >
              <Button
                variant="link"
                size="xs"
                colorScheme="gray"
                onClick={handleClear}
              >
                Clear
              </Button>
              <Button size="xs" colorScheme="brand" onClick={onClose}>
                Done
              </Button>
            </HStack>
          </PopoverBody>
        </PopoverContent>
      </Popover>

      {siteIds.length > 0 && (
        <Wrap spacing={1.5} mt={2}>
          {siteIds.map((id) => (
            <WrapItem key={id}>
              <Tag colorScheme="gray" borderRadius="md">
                <TagLabel>{siteNameMap.get(id) || id}</TagLabel>
                <TagCloseButton onClick={() => onToggleSite(id)} />
              </Tag>
            </WrapItem>
          ))}
        </Wrap>
      )}
    </Box>
  );
}
